import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { Button } from '@ridemaadi/ui';
import { shellStyles } from '../../components/layout/shellLayout';

export interface RideStartedBannerPayload {
  rideId: string;
  rideTitle: string;
  startedByName: string;
}

/** In-app banner when a joined ride starts while the app is open. */
export function RideStartedBanner({
  payload,
  onDismiss,
  onOpen,
}: {
  payload: RideStartedBannerPayload;
  onDismiss: () => void;
  onOpen: () => void;
}) {
  return (
    <div
      className="fixed inset-x-0 z-[90] border-b border-success/30 bg-success/10 px-4 py-3 shadow-md backdrop-blur-md"
      style={{ top: shellStyles.bannerTop }}
      role="status"
      aria-live="polite"
    >
      <div className="mx-auto flex max-w-app items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-content">Ride Started</p>
          <p className="text-sm text-muted">
            🏍 {payload.rideTitle} has started. Started by {payload.startedByName}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button size="sm" onClick={onOpen}>
            Open Ride
          </Button>
          <button
            type="button"
            onClick={onDismiss}
            className="flex min-h-touch min-w-touch items-center justify-center text-muted"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Global banner state for ride-started in-app notifications. */
export function useRideStartedBanner() {
  const [payload, setPayload] = useState<RideStartedBannerPayload | null>(null);
  const navigate = useNavigate();

  const show = useCallback((next: RideStartedBannerPayload) => {
    if (document.visibilityState !== 'visible') return;
    setPayload(next);
  }, []);

  const dismiss = useCallback(() => setPayload(null), []);

  const open = useCallback(() => {
    if (!payload) return;
    navigate(`/rides/${payload.rideId}`);
    setPayload(null);
  }, [navigate, payload]);

  useEffect(() => {
    if (!payload) return;
    const timer = setTimeout(() => setPayload(null), 12_000);
    return () => clearTimeout(timer);
  }, [payload]);

  return { payload, show, dismiss, open };
}
